import { useState } from "react";
import { useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { AccessDenied } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Instagram,
  Megaphone,
  BookOpen,
  Check,
  Copy,
  ExternalLink,
  AlertTriangle,
  Settings as SettingsIcon,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Manual de conexão do Instagram e do Meta Ads.
 *
 * Existe como página do produto, e não como texto de suporte, porque a parte
 * difícil dessa conexão acontece FORA daqui — no app do Instagram, na Página do
 * Facebook e no Gerenciador de Negócios. Quem trava, trava lá, e descobre isso
 * por uma mensagem de erro que não explica o que fazer. Este manual é o mapa
 * dessas etapas externas, com os motivos, para o admin não precisar adivinhar.
 *
 * O texto é para o CLIENTE, não para quem mantém o código: nada de escopo,
 * HMAC ou token de Página. A contraparte técnica é `docs/login-facebook.md`.
 */

/** Passo numerado. O número à esquerda é o que dá a sensação de progresso. */
function Passo({
  n,
  titulo,
  children,
}: {
  n: number;
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {n}
      </div>
      <div className="min-w-0 flex-1 space-y-2 pb-6">
        <h3 className="text-sm font-semibold leading-7">{titulo}</h3>
        <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

/** Caixa de "por que isso importa" — o que evita o chamado de suporte. */
function Porque({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded border-l-2 border-primary/40 bg-muted/40 px-3 py-2 text-xs">
      {children}
    </p>
  );
}

function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function UrlCopiavel({ url }: { url: string }) {
  return (
    <div className="flex items-center gap-2 rounded border bg-muted/50 p-2">
      <code className="min-w-0 flex-1 truncate text-xs">{url}</code>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          void navigator.clipboard
            .writeText(url)
            .then(() => toast.success("Endereço copiado."))
            .catch(() => toast.error("Não consegui copiar. Selecione e copie à mão."));
        }}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function ConectarMeta() {
  const { can } = usePermissions();
  const [, navigate] = useLocation();
  const podeInstagram = can("instagram:manage_connection");
  const podeAds = can("ads:credentials_manage");

  // A aba inicial é a que a pessoa pode de fato usar. Abrir em Instagram para
  // quem só cuida de anúncios seria mostrar uma tela inútil.
  const [aba, setAba] = useState(podeInstagram ? "instagram" : "ads");

  const webhookUrl = `${window.location.origin}/api/webhooks/instagram`;

  if (!podeInstagram && !podeAds) {
    return (
      <DashboardLayout>
        <AccessDenied />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <BookOpen className="h-5 w-5 text-primary" />
            Como conectar Instagram e Anúncios
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            A maior parte deste caminho acontece do lado da Meta, não aqui. Siga na ordem — cada
            passo depende do anterior estar pronto.
          </p>
        </div>

        <Tabs value={aba} onValueChange={setAba}>
          <TabsList>
            {podeInstagram && (
              <TabsTrigger value="instagram">
                <Instagram className="mr-2 h-4 w-4" />
                Instagram
              </TabsTrigger>
            )}
            {podeAds && (
              <TabsTrigger value="ads">
                <Megaphone className="mr-2 h-4 w-4" />
                Anúncios
              </TabsTrigger>
            )}
          </TabsList>

          {/* ── Instagram ──────────────────────────────────────────────────── */}
          <TabsContent value="instagram" className="mt-4 space-y-4">
            <Card>
              <CardContent className="pt-6">
                <p className="mb-6 text-sm">
                  Ao final, as mensagens diretas do seu Instagram vão cair no Atendimento junto com
                  as do WhatsApp, e os comentários dos seus posts vão para uma fila onde dá para
                  responder.
                </p>

                <Passo n={1} titulo="Deixe a conta do Instagram como Profissional">
                  <p>
                    No app do Instagram: <strong>Perfil → menu (☰) → Configurações → Tipo de conta
                    e ferramentas → Mudar para conta profissional</strong>. Serve tanto Comercial
                    quanto Criador de conteúdo.
                  </p>
                  <Porque>
                    Conta pessoal não tem API de mensagens. Se este passo faltar, os passos
                    seguintes até parecem funcionar, mas a conta simplesmente não aparece na lista
                    na hora de escolher.
                  </Porque>
                </Passo>

                <Passo n={2} titulo="Vincule a conta a uma Página do Facebook">
                  <p>
                    No mesmo menu: <strong>Central de contas → Adicionar Página do Facebook</strong>
                    . Se você ainda não tem uma Página, crie uma — ela pode ficar sem nenhuma
                    publicação.
                  </p>
                  <Porque>
                    A Meta entrega as mensagens do Instagram através da Página vinculada. É por isso
                    que existe uma Página no meio de uma integração que, para você, é só do
                    Instagram.
                  </Porque>
                </Passo>

                <Passo n={3} titulo="Confirme que você administra a Página">
                  <p>
                    Em <strong>facebook.com/settings → Páginas</strong>, sua conta precisa aparecer
                    como <strong>Administrador</strong> da Página — não Editor, não Moderador.
                  </p>
                  <Porque>
                    Só administrador consegue autorizar o acesso às mensagens. Com outro cargo, a
                    autorização é concedida pela metade e as DMs nunca chegam.
                  </Porque>
                </Passo>

                <Passo n={4} titulo="Conecte aqui no LeadConversa">
                  <p>
                    Vá em <strong>Configurações → Integrações → Instagram</strong> e use um dos dois
                    caminhos:
                  </p>
                  <div className="space-y-2 pt-1">
                    <div className="rounded border p-3">
                      <p className="text-xs font-semibold text-foreground">
                        Entrar com Facebook <span className="font-normal">(mais simples)</span>
                      </p>
                      <p className="mt-1 text-xs">
                        Você é levado para a tela da Meta, autoriza, escolhe a página e volta pronto.
                        Mantenha <strong>todas as permissões marcadas</strong> na tela de
                        autorização — desmarcar uma faz a conexão ser recusada aqui.
                      </p>
                    </div>
                    <div className="rounded border p-3">
                      <p className="text-xs font-semibold text-foreground">
                        Colar um token <span className="font-normal">(alternativa)</span>
                      </p>
                      <p className="mt-1 text-xs">
                        Se o botão do Facebook não estiver disponível no seu ambiente, gere um token
                        de acesso da Página e cole no campo. Depois clique em{" "}
                        <strong>Procurar contas</strong> e escolha a página.
                      </p>
                    </div>
                  </div>
                  <div className="pt-2">
                    <Button size="sm" onClick={() => navigate("/settings?integration=instagram")}>
                      <SettingsIcon className="mr-2 h-4 w-4" />
                      Abrir a tela de conexão
                    </Button>
                  </div>
                </Passo>

                <Passo n={5} titulo="Ligue o recebimento das mensagens na Meta">
                  <p>
                    No painel do app em <strong>developers.facebook.com</strong>, produto{" "}
                    <strong>Instagram → Webhooks</strong>, aponte para este endereço:
                  </p>
                  <UrlCopiavel url={webhookUrl} />
                  <p>
                    E assine os campos <code>messages</code>, <code>comments</code>,{" "}
                    <code>mentions</code> e <code>messaging_postbacks</code> — este último é o
                    que traz o toque nos botões das automações ("Quero o link", "Já estou
                    seguindo"); sem ele o botão não faz nada.
                  </p>
                  <Aviso>
                    <p>
                      <strong>Este é o passo que mais fica esquecido.</strong> Sem ele a conexão
                      aparece como "conectada" e o teste passa — mas nenhuma mensagem chega, porque
                      a Meta não tem para onde enviá-las.
                    </p>
                  </Aviso>
                </Passo>

                <Passo n={6} titulo="Teste de ponta a ponta">
                  <p>
                    Clique em <strong>Testar conexão</strong> na tela de Integrações e depois mande
                    uma mensagem para o seu Instagram de outro celular. Ela deve aparecer no
                    Atendimento em poucos segundos, com o selo do canal.
                  </p>
                  <Porque>
                    O "Testar conexão" confirma que conseguimos falar com a Meta. Só a mensagem de
                    verdade confirma que a Meta consegue falar conosco — são coisas diferentes, e é
                    a segunda que costuma faltar.
                  </Porque>
                </Passo>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 pt-6">
                <h2 className="text-sm font-semibold">O que esperar depois de conectado</h2>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      As DMs entram na mesma caixa do WhatsApp, identificadas por <code>@usuário</code>{" "}
                      em vez de telefone.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      <strong>Você tem 24 horas para responder</strong>, contadas da última mensagem
                      da pessoa. É regra da Meta, não nossa. A tela avisa quando o prazo está
                      acabando, antes de você escrever.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      Comentários dos posts vão para <strong>Instagram → Comentários</strong>, onde
                      dá para responder em público, chamar no privado ou ocultar.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <span>
                      <strong>Robô, fluxos e automações ainda não atendem o Instagram</strong> — eles
                      dependem do número de telefone, que uma conta de Instagram não tem. As DMs
                      chegam para atendimento humano.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <span>
                      <strong>Anexar arquivo na resposta ainda não funciona no Instagram.</strong> A
                      Meta busca o arquivo por um endereço público, e os anexos daqui ficam em área
                      protegida. Envie o link no texto.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Anúncios ───────────────────────────────────────────────────── */}
          <TabsContent value="ads" className="mt-4 space-y-4">
            <Card>
              <CardContent className="pt-6">
                <p className="mb-6 text-sm">
                  Ao final, você vê o gasto e o resultado das suas campanhas na tela de Anúncios, e
                  pode criar e ajustar campanhas conversando com o assistente.
                </p>

                <Passo n={1} titulo="Tenha a conta de anúncios num Gerenciador de Negócios">
                  <p>
                    Em <strong>business.facebook.com</strong>, confirme que a conta de anúncios que
                    você quer usar aparece em <strong>Configurações → Contas → Contas de
                    anúncios</strong>.
                  </p>
                  <Porque>
                    Conta de anúncios pessoal, fora de um Gerenciador de Negócios, não pode ser
                    acessada por nenhuma ferramenta externa — nem por esta.
                  </Porque>
                </Passo>

                <Passo n={2} titulo="Conecte aqui no LeadConversa">
                  <p>
                    Vá em <strong>Configurações → Integrações → Meta Ads</strong>. O caminho mais
                    simples é <strong>Entrar com Facebook</strong>: você autoriza na tela da Meta e
                    volta com suas contas de anúncio já listadas.
                  </p>
                  <p>
                    Se preferir o caminho manual, use um <strong>Usuário do Sistema</strong> — a
                    seção abaixo tem o passo a passo.
                  </p>
                  <div className="pt-2">
                    <Button size="sm" onClick={() => navigate("/settings?integration=meta_ads")}>
                      <SettingsIcon className="mr-2 h-4 w-4" />
                      Abrir a tela de conexão
                    </Button>
                  </div>
                </Passo>

                <Passo n={3} titulo="Escolha a conta padrão">
                  <p>
                    Se você administra mais de uma conta de anúncios, marque qual é a padrão. É a
                    que a tela de Anúncios abre e a que o assistente usa quando você não diz qual.
                  </p>
                </Passo>

                <Passo n={4} titulo="Defina um teto de orçamento diário">
                  <p>
                    No mesmo painel, preencha o <strong>teto de orçamento diário</strong>. Nenhuma
                    campanha criada por aqui poderá passar desse valor.
                  </p>
                  <Aviso>
                    <p>
                      <strong>Recomendamos fortemente preencher.</strong> Aqui se mexe com verba
                      real. O teto é o que impede um zero a mais — digitado por você ou pelo
                      assistente — virar gasto de verdade.
                    </p>
                  </Aviso>
                </Passo>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 pt-6">
                <h2 className="text-sm font-semibold">Como criar campanhas com segurança</h2>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      <strong>Toda campanha criada por aqui nasce pausada.</strong> Ela não começa a
                      gastar até você mandar ativar, numa ação separada.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      Ativar exige uma permissão própria (<em>ativar anúncios e alterar
                      orçamento</em>). Quem só acompanha resultado não precisa dela.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      Nada é excluído por aqui — o máximo que acontece com uma campanha é ser
                      arquivada.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <span>
                      Os números seguem o <strong>fuso e a moeda da conta de anúncios</strong>, não
                      os seus. Se "ontem" aqui não bater com o Gerenciador de Anúncios, quase sempre
                      é isso.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Accordion type="single" collapsible>
                  <AccordionItem value="system-user" className="border-none">
                    <AccordionTrigger className="py-0 text-sm font-semibold hover:no-underline">
                      Caminho manual: criar um Usuário do Sistema
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <div className="space-y-3 text-sm text-muted-foreground">
                        <p>
                          Use este caminho se o botão <strong>Entrar com Facebook</strong> não
                          estiver disponível no seu ambiente.
                        </p>
                        <ol className="list-decimal space-y-2 pl-5">
                          <li>
                            Em <strong>business.facebook.com → Configurações → Usuários → Usuários
                            do sistema</strong>, clique em <strong>Adicionar</strong> e crie um com
                            função <strong>Administrador</strong>.
                          </li>
                          <li>
                            Em <strong>Adicionar ativos</strong>, dê a ele acesso à conta de
                            anúncios, com <strong>controle total</strong>.
                          </li>
                          <li>
                            Clique em <strong>Gerar novo token</strong>, escolha o seu app e marque{" "}
                            <code>ads_management</code>, <code>ads_read</code> e{" "}
                            <code>business_management</code>.
                          </li>
                          <li>
                            Copie o token e cole no campo da tela de Integrações. Ele é guardado
                            criptografado e nunca mais aparece na tela.
                          </li>
                        </ol>
                        <Aviso>
                          <p>
                            Este token <strong>não expira</strong>. É o que o torna cômodo e, ao
                            mesmo tempo, o que exige cuidado: quem tiver acesso a ele mexe na sua
                            verba. Não o compartilhe por e-mail nem por mensagem.
                          </p>
                        </Aviso>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ── Quando algo dá errado ────────────────────────────────────────── */}
        <Card>
          <CardContent className="space-y-4 pt-6">
            <h2 className="text-sm font-semibold">Quando algo dá errado</h2>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="nao-chega">
                <AccordionTrigger className="text-sm">
                  Conectei o Instagram, mas nenhuma mensagem chega
                </AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    Em quase todos os casos é o <strong>passo 5</strong>: o webhook não foi
                    configurado no painel da Meta, ou foi configurado sem assinar o campo{" "}
                    <code>messages</code>.
                  </p>
                  <p>
                    Confira também se a mensagem de teste veio de <strong>outra conta</strong>:
                    mensagem que você mesmo envia pela sua conta não entra como recebida.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="sem-conta">
                <AccordionTrigger className="text-sm">
                  Minha conta do Instagram não aparece na lista
                </AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>Três causas, nesta ordem de frequência:</p>
                  <ol className="list-decimal space-y-1 pl-5">
                    <li>A conta ainda é pessoal, não profissional (passo 1).</li>
                    <li>Ela não está vinculada a nenhuma Página do Facebook (passo 2).</li>
                    <li>Você não é administrador da Página, só editor (passo 3).</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ja-conectada">
                <AccordionTrigger className="text-sm">
                  "Esta conta já está conectada a outro workspace"
                </AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    Uma conta do Instagram só pode estar ligada a um workspace por vez — se duas
                    apontassem para a mesma conta, as mensagens de uma apareceriam na outra.
                  </p>
                  <p>
                    Desconecte no workspace antigo e conecte aqui. Se você não sabe qual é,
                    fale com o suporte.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="prazo">
                <AccordionTrigger className="text-sm">
                  "O prazo para responder expirou"
                </AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    A Meta só permite mensagem livre nas 24 horas seguintes à última mensagem da
                    pessoa. Passou disso, é preciso esperar ela escrever de novo — não há como
                    contornar pelo produto.
                  </p>
                  <p>
                    Existe uma permissão da Meta que estende esse prazo para 7 dias em atendimento
                    humano. Ela exige aprovação da Meta e, uma vez aprovada, é ligada no painel de
                    Integrações.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="sem-contas-ads">
                <AccordionTrigger className="text-sm">
                  Conectei os anúncios, mas nenhuma conta apareceu
                </AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    A autorização funcionou, mas a conta do Facebook usada não administra nenhuma
                    conta de anúncios dentro de um Gerenciador de Negócios.
                  </p>
                  <p>
                    Peça acesso à conta de anúncios em <strong>business.facebook.com</strong> e
                    conecte de novo.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="numeros">
                <AccordionTrigger className="text-sm">
                  Os números não batem com o Gerenciador de Anúncios
                </AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    Compare o mesmo período no mesmo fuso. Nós mostramos os dados no fuso da{" "}
                    <strong>conta de anúncios</strong> — se ela está em outro fuso que o seu, "hoje"
                    e "ontem" começam em horas diferentes.
                  </p>
                  <p>
                    A Meta também consolida números por algumas horas: dados do dia corrente mudam
                    ao longo do dia dos dois lados.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="expirou">
                <AccordionTrigger className="text-sm">
                  Funcionava e parou do nada
                </AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    O acesso concedido à Meta cai quando a pessoa que autorizou troca a senha,
                    remove o app ou perde o cargo na Página ou na conta de anúncios.
                  </p>
                  <p>
                    Abra Integrações, clique em <strong>Testar conexão</strong> para confirmar, e
                    reconecte. Nenhuma configuração sua se perde nisso.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <p className="flex items-center gap-1.5 border-t pt-4 text-xs text-muted-foreground">
              <ExternalLink className="h-3.5 w-3.5" />
              Continuou travado? Guarde o horário em que tentou e o que apareceu na tela — é o que
              permite achar o registro exato do que aconteceu.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
